import { API_BASE } from '@/config';
import { fetchWithSession } from './sessionFetch';

export type CurrentOrganizationRole = 'owner' | 'admin' | 'member';

export interface CurrentOrganization {
  org_id: string;
  org_role: CurrentOrganizationRole;
}

export interface CurrentIdentitySession {
  getSessionJwt: () => string | undefined;
  refreshSession: () => Promise<void>;
}

const organizationRoles = new Set<CurrentOrganizationRole>([
  'owner',
  'admin',
  'member',
]);

/**
 * Only what the app reads off `/auth/current`. The wire also carries the
 * Stytch `provider` and `provider_organization_id`, and — until one release
 * after ENG-2412 — the retired `organization.type`; nothing here needs them,
 * so they are neither typed nor validated and the backend can drop them
 * without a frontend change.
 */
export interface CurrentIdentity {
  user_id: string;
  organization: CurrentOrganization;
}

function nonEmptyString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Invalid current identity: ${field}`);
  }
  return value;
}

export function parseCurrentIdentity(value: unknown): CurrentIdentity {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid current identity response');
  }
  const identity = value as Record<string, unknown>;
  if (
    !identity.organization ||
    typeof identity.organization !== 'object' ||
    Array.isArray(identity.organization)
  ) {
    throw new Error('Invalid current identity: organization');
  }
  const source = identity.organization as Record<string, unknown>;
  if (
    typeof source.org_role !== 'string' ||
    !organizationRoles.has(source.org_role as CurrentOrganizationRole)
  ) {
    throw new Error('Invalid current identity: organization.org_role');
  }
  return {
    user_id: nonEmptyString(identity.user_id, 'user_id'),
    organization: {
      org_id: nonEmptyString(source.org_id, 'organization.org_id'),
      org_role: source.org_role as CurrentOrganizationRole,
    },
  };
}

export function authCurrentUrl(apiBase = API_BASE): string {
  return `${apiBase.replace(/\/$/, '')}/auth/current`;
}

export async function fetchCurrentIdentity(
  session: CurrentIdentitySession,
  fetchImpl: typeof fetch = fetch,
): Promise<CurrentIdentity> {
  const response = await fetchWithSession(
    authCurrentUrl(),
    {
      method: 'GET',
      cache: 'no-store',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
    session,
    fetchImpl,
  );
  if (!response.ok) {
    throw new Error(`Current identity request failed (${response.status})`);
  }

  return parseCurrentIdentity(await response.json());
}
